import React, {useCallback, useMemo, useState} from "react";
import {ExclamationCircleOutlined, LockOutlined} from "@ant-design/icons";
import {useUpdatePasswordMutation} from "../services/nestjs-base-api";
import {Button, Form, Input, message, theme, Typography} from "antd";
import {useNavigate, useParams} from "react-router-dom";
import {FormattedMessage} from "react-intl";
import {useIntl} from "react-intl";
import AuthLayout from "../components/AuthLayout";
import styled from "styled-components";

const { useToken } = theme;
const { Text, Link } = Typography;

const StyledForm = styled(Form)`
  .ant-form-item {
    margin-bottom: 24px;
  }
  
  .ant-input-affix-wrapper,
  .ant-input {
    height: 48px;
    font-size: 16px;
    border-radius: 8px;
    border: 1px solid #d9d9d9;
    
    &:hover,
    &:focus {
      border-color: #4169e1;
      box-shadow: 0 0 0 2px rgba(65, 105, 225, 0.1);
    }
  }
  
  .ant-input-prefix {
    color: #4169e1;
    font-size: 18px;
    margin-right: 12px;
  }
`;

const StyledButton = styled(Button)`
  height: 48px;
  font-size: 16px;
  font-weight: 500;
  border-radius: 8px;
  background: linear-gradient(135deg, #4169e1 0%, #1890ff 100%);
  border: none;
  box-shadow: 0 4px 12px rgba(65, 105, 225, 0.3);
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 6px 16px rgba(65, 105, 225, 0.4);
  }
  
  &:disabled {
    background: #f5f5f5;
    box-shadow: none;
    transform: none;
  }
`;

const FooterWrapper = styled.div`
  margin-top: 24px;
  text-align: center;
`;

const StyledLink = styled(Link)`
  color: #4169e1;
  font-weight: 500;
  
  &:hover {
    color: #1890ff;
  }
`;

const PasswordSetPage: React.FunctionComponent = () => {
    const intl = useIntl();
    const { token } = useToken();
    const navigate = useNavigate();
    const [updatePassword, updatePasswordResult] = useUpdatePasswordMutation();
    const [messageApi, contextHolder] = message.useMessage();
    const [hideForm, setHideForm] = useState<boolean>(false);
    const { resetToken } = useParams();

    const onUpdateError = useCallback(() => {
        messageApi.open({
            type: 'error',
            content: <FormattedMessage id='passwordSet.failed' />,
            className: 'custom-class',
            style: {
                marginTop: '1vh',
                fontSize: '1.2rem'
            },
            duration: 4,
            icon: <ExclamationCircleOutlined style={{fontSize: '1.2rem', paddingBottom: '0.1rem'}} />
        });
    }, [messageApi]);

    const handleUpdatePassword = useCallback(async (values: any) => {
        const updateResponse = await updatePassword({token: resetToken!, password: values.password});

        if ('data' in updateResponse) {
            setHideForm(true);
        } else {
            onUpdateError();
        }
    }, [onUpdateError, resetToken, updatePassword]);

    const signInLink = useMemo(() => {
        return <StyledLink onClick={() => navigate('/sign-in')}><FormattedMessage id='passwordSet.success.signIn'/></StyledLink>
    }, [navigate]);

    return (
        <>
            {contextHolder}
            <AuthLayout
                title={hideForm 
                    ? intl.formatMessage({ id: 'passwordSet.success.title' }) 
                    : intl.formatMessage({ id: 'passwordSet.title' })
                }
                subtitle={hideForm 
                    ? intl.formatMessage({ id: 'passwordSet.success.welcome' }, {signInLink: signInLink as unknown as string})
                    : intl.formatMessage({ id: 'passwordSet.welcome' })
                }
            >
                <div style={{display: hideForm ? 'none' : 'block'}}>
                    <StyledForm
                        name="normal_set"
                        onFinish={handleUpdatePassword}
                        layout="vertical"
                        requiredMark="optional"
                    >
                        <Form.Item
                            name="password"
                            hasFeedback
                            rules={[
                                {
                                    required: true,
                                    message: intl.formatMessage({ id: 'passwordSet.form.password.error' }),
                                    min: 8
                                },
                            ]}
                        >
                            <Input.Password
                                prefix={<LockOutlined/>}
                                type="password"
                                placeholder={intl.formatMessage({ id: 'passwordSet.form.password' })}
                            />
                        </Form.Item>
                        <Form.Item
                            name="confirm"
                            dependencies={['password']}
                            hasFeedback
                            rules={[
                                {
                                    required: true,
                                    message: intl.formatMessage({ id: 'passwordSet.form.confirmPassword.missing' }),
                                },
                                ({ getFieldValue }) => ({
                                    validator(_, value) {
                                        if (!value || getFieldValue('password') === value) {
                                            return Promise.resolve();
                                        }
                                        return Promise.reject(new Error(intl.formatMessage({ id: 'passwordSet.form.password.mismatch' })));
                                    },
                                }),
                            ]}
                        >
                            <Input.Password
                                prefix={<LockOutlined/>}
                                type="password"
                                placeholder={intl.formatMessage({ id: 'passwordSet.form.confirmPassword' })}
                            />
                        </Form.Item>
                        <Form.Item>
                            <StyledButton 
                                block={true} 
                                type="primary" 
                                htmlType="submit"
                                disabled={updatePasswordResult.isLoading}
                                loading={updatePasswordResult.isLoading}
                            >
                                <FormattedMessage id='passwordSet.form.submit' />
                            </StyledButton>
                            <FooterWrapper>
                                <Text style={{ color: token.colorTextSecondary }}>
                                    <FormattedMessage id='passwordSet.problems' />
                                </Text>{" "}
                                <StyledLink onClick={() => navigate('/')}>
                                    <FormattedMessage id='passwordSet.contact' />
                                </StyledLink>
                            </FooterWrapper>
                        </Form.Item>
                    </StyledForm>
                </div>
                <div style={{display: hideForm ? 'block' : 'none'}}>
                    {/* Success message is handled by the title/subtitle in AuthLayout */}
                </div>
            </AuthLayout>
        </>
    );
}

export default PasswordSetPage;